import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Phone, PhoneOff, Video, VideoOff, Mic, MicOff, 
  X, User, PhoneIncoming, PhoneOutgoing
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ICE servers for WebRTC (use TURN servers in production for better connectivity)
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

export const useWebRTCCall = (userId, userName, userType = 'dispatcher') => {
  const [callState, setCallState] = useState('idle'); // idle, calling, ringing, connected
  const [currentCall, setCurrentCall] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  
  const peerConnection = useRef(null);
  const pollingInterval = useRef(null);

  // Poll for incoming calls and signals
  const pollSignals = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${API}/calls/pending/${userId}`);
      const data = await response.json();
      
      for (const signal of data.signals || []) {
        if (signal.type === 'incoming_call' && callState === 'idle') {
          setIncomingCall(signal.call_data);
          setCallState('ringing');
        } else if (signal.type === 'offer' && peerConnection.current) {
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(signal.signal_data));
          const answer = await peerConnection.current.createAnswer();
          await peerConnection.current.setLocalDescription(answer);
          
          await fetch(`${API}/calls/${signal.call_id}/signal`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              caller_id: userId,
              caller_name: userName,
              caller_type: userType,
              recipient_id: signal.from_id,
              recipient_type: 'dispatcher',
              signal_type: 'answer',
              signal_data: answer
            })
          });
        } else if (signal.type === 'answer' && peerConnection.current) {
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(signal.signal_data));
        } else if (signal.type === 'ice-candidate' && peerConnection.current) {
          await peerConnection.current.addIceCandidate(new RTCIceCandidate(signal.signal_data));
        } else if (signal.type === 'call_ended' || signal.type === 'call_rejected') {
          endCall();
        }
      }
    } catch (error) {
      console.error('Error polling signals:', error);
    }
  }, [userId, userName, userType, callState]);

  useEffect(() => {
    if (userId) {
      pollingInterval.current = setInterval(pollSignals, 1000);
    }
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [userId, pollSignals]);

  const initializePeerConnection = async (callType = 'voice') => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    pc.onicecandidate = async (event) => {
      if (event.candidate && currentCall) {
        await fetch(`${API}/calls/${currentCall.id}/signal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            caller_id: userId,
            caller_name: userName,
            caller_type: userType,
            recipient_id: currentCall.recipient_id || currentCall.caller_id,
            recipient_type: 'driver',
            signal_type: 'ice-candidate',
            signal_data: event.candidate
          })
        });
      }
    };

    pc.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setCallState('connected');
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        endCall();
      }
    };

    // Get local media
    try {
      const constraints = {
        audio: true,
        video: callType === 'video'
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(stream);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
    } catch (error) {
      console.error('Error accessing media devices:', error);
    }

    peerConnection.current = pc;
    return pc;
  };

  const startCall = async (recipientId, recipientName, callType = 'voice') => {
    try {
      setCallState('calling');
      
      // Initiate call on backend
      const response = await fetch(`${API}/calls/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caller_id: userId,
          caller_name: userName,
          caller_type: userType,
          recipient_id: recipientId,
          recipient_type: 'driver',
          call_type: callType
        })
      });
      
      const callData = await response.json();
      setCurrentCall({ ...callData, recipient_id: recipientId, recipient_name: recipientName });
      
      // Initialize WebRTC
      const pc = await initializePeerConnection(callType);
      
      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      await fetch(`${API}/calls/${callData.call_id}/signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caller_id: userId,
          caller_name: userName,
          caller_type: userType,
          recipient_id: recipientId,
          recipient_type: 'driver',
          signal_type: 'offer',
          signal_data: offer
        })
      });
      
    } catch (error) {
      console.error('Error starting call:', error);
      setCallState('idle');
    }
  };

  const answerCall = async () => {
    if (!incomingCall) return;
    
    try {
      setCurrentCall(incomingCall);
      setCallState('connected');
      
      await fetch(`${API}/calls/${incomingCall.id}/answer`, { method: 'PUT' });
      await initializePeerConnection(incomingCall.call_type);
      
      setIncomingCall(null);
    } catch (error) {
      console.error('Error answering call:', error);
    }
  };

  const rejectCall = async () => {
    if (!incomingCall) return;
    
    try {
      await fetch(`${API}/calls/${incomingCall.id}/reject`, { method: 'PUT' });
      setIncomingCall(null);
      setCallState('idle');
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  };

  const endCall = async () => {
    if (currentCall) {
      try {
        await fetch(`${API}/calls/${currentCall.id}/end?user_id=${userId}`, { method: 'PUT' });
      } catch (error) {
        console.error('Error ending call:', error);
      }
    }
    
    // Clean up
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (peerConnection.current) {
      peerConnection.current.close();
    }
    
    setLocalStream(null);
    setRemoteStream(null);
    setCurrentCall(null);
    setCallState('idle');
    peerConnection.current = null;
  };

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  return {
    callState,
    currentCall,
    incomingCall,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    startCall,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo
  };
};

// Call UI Component
export const CallInterface = ({ 
  callState, 
  currentCall, 
  incomingCall,
  localStream,
  remoteStream,
  isMuted,
  isVideoOff,
  onAnswer,
  onReject,
  onEnd,
  onToggleMute,
  onToggleVideo
}) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Incoming call overlay
  if (callState === 'ringing' && incomingCall) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <Card className="w-80 glass-card p-6 text-center">
          <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <PhoneIncoming className="w-10 h-10 text-blue-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Incoming Call</h3>
          <p className="text-slate-400 mb-6">{incomingCall.caller_name}</p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={onReject}
              className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600"
            >
              <PhoneOff className="w-6 h-6" />
            </Button>
            <Button
              onClick={onAnswer}
              className="w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600"
            >
              <Phone className="w-6 h-6" />
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Active call overlay
  if (callState === 'calling' || callState === 'connected') {
    const isVideo = currentCall?.call_type === 'video';
    
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex flex-col">
        {/* Video area */}
        <div className="flex-1 relative">
          {isVideo ? (
            <>
              {/* Remote video (full screen) */}
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              {/* Local video (picture-in-picture) */}
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="absolute bottom-4 right-4 w-32 h-24 object-cover rounded-lg border-2 border-white/20"
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-32 h-32 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <User className="w-16 h-16 text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {currentCall?.recipient_name || currentCall?.caller_name || 'Unknown'}
                </h3>
                <p className="text-slate-400">
                  {callState === 'calling' ? 'Calling...' : 'Connected'}
                </p>
                {/* Hidden audio element for voice calls */}
                <audio ref={remoteVideoRef} autoPlay />
              </div>
            </div>
          )}
        </div>

        {/* Call controls */}
        <div className="p-6 bg-gradient-to-t from-black/80">
          <div className="flex justify-center gap-4">
            <Button
              onClick={onToggleMute}
              className={`w-14 h-14 rounded-full ${isMuted ? 'bg-red-500' : 'bg-slate-700'}`}
            >
              {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </Button>
            
            {isVideo && (
              <Button
                onClick={onToggleVideo}
                className={`w-14 h-14 rounded-full ${isVideoOff ? 'bg-red-500' : 'bg-slate-700'}`}
              >
                {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
              </Button>
            )}
            
            <Button
              onClick={onEnd}
              className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600"
            >
              <PhoneOff className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Call button component for easy integration
export const CallButton = ({ recipientId, recipientName, onStartCall, variant = 'voice' }) => {
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => onStartCall(recipientId, recipientName, variant)}
      className="gap-1"
      data-testid={`call-${variant}-${recipientId}`}
    >
      {variant === 'video' ? (
        <Video className="w-4 h-4" />
      ) : (
        <Phone className="w-4 h-4" />
      )}
      {variant === 'video' ? 'Video' : 'Call'}
    </Button>
  );
};

export default CallInterface;
